import { LoaderFunctionArgs, MetaFunction, redirect } from '@remix-run/node'
import {
	Link,
	defer,
	isRouteErrorResponse,
	useLoaderData,
	useRouteError,
} from '@remix-run/react'
import {
	getAttestationv2,
	getDerivedAttestationsForRollupCID,
	getEventDataByRollupCID,
} from '#app/server/db.server.ts'
import {
	AttestationType,
	eventData,
	nft,
	NFTCache,
	User,
} from '#types/loreTypes.tsx'
import Button from '#app/components/buttons/button'
import Section from '#app/components/layout/section'
import TypographyParagraph from '#app/components/typography/TypographyParagraph'
import { formatString } from '#app/utils/misc.tsx'
import videoPlaceholder from '#images/video-placeholder.jpg'
import { captureRemixErrorBoundaryError } from '@sentry/remix'
import EventMetadataProfile from '#app/components/metadataProfile/eventMetadataProfile.tsx'
import { getDomainUrl } from '#app/utils/misc.server'

export const meta: MetaFunction<typeof loader> = ({
	data,
	params,
	location,
	matches,
}) => {
	const event = data?.event
	const origin = data?.origin || 'https://atomiclore.io'
	if (!event) {
		return [
			{ title: 'Atomic Lore' },
			{ property: 'og:title', content: 'Atomic Lore' },
			{
				name: 'description',
				content:
					'The first Web3 directory showcasing offchain history that is cryptographically signed, stored, and beautifully displayed.',
			},
			{
				property: 'og:description',
				content:
					'The first Web3 directory showcasing offchain history that is cryptographically signed, stored, and beautifully displayed.',
			},
			{ property: 'og:site_name', content: 'atomiclore.io' },
			{ name: 'twitter:card', content: 'summary_large_image' },
			{ name: 'twitter:site', content: '@atomicform' },
			{ name: 'twitter:creator', content: '@atomicform' },
			{
				name: 'twitter:description',
				content:
					'The first Web3 directory showcasing offchain history that is cryptographically signed, stored, and beautifully displayed.',
			},
			{ name: 'twitter:title', content: 'Atomic Lore' },
		]
	} else {
		const path = location.pathname
		return [
			{
				title: event
					? `Atomic Lore for ${event.eventTitle}`
					: import.meta.env.VITE_APP_NAME
						? import.meta.env.VITE_APP_NAME
						: 'Atomic Lore',
			},
			{
				property: 'og:title',
				content: event
					? `Atomic Lore for ${event.eventTitle}`
					: import.meta.env.VITE_APP_NAME
						? import.meta.env.VITE_APP_NAME
						: 'Atomic Lore',
			},
			{ property: 'og:url', content: origin + path },
			{
				name: 'description',
				content: event
					? `${event.eventDescription} | Powered by Atomic Lore, the first Web3 directory showcasing offchain history that is cryptographically signed, stored, and beautifully displayed.`
					: import.meta.env.VITE_APP_DESCRIPTION
						? import.meta.env.VITE_APP_DESCRIPTION
						: 'A tool to explore NFT history, by Atomic Form',
			},
			{
				property: 'og:description',
				content: event
					? `${event.eventDescription} | Powered by Atomic Lore, the first Web3 directory showcasing offchain history that is cryptographically signed, stored, and beautifully displayed.`
					: import.meta.env.VITE_APP_DESCRIPTION
						? import.meta.env.VITE_APP_DESCRIPTION
						: 'A tool to explore NFT history, by Atomic Form',
			},
			{ property: 'og:site_name', content: 'atomiclore.io' },
			// IMAGE
			{ name: 'image', content: event.eventImageUrl },
			{
				property: 'og:image',
				content: event.eventImageUrl,
			},
			{ property: 'og:image:width', content: '1200px' },
			{ property: 'og:image:height', content: '630px' },
			// TWITTER / SLACK
			{
				name: 'twitter:image',
				content: event.ogimage,
			},
			{ name: 'twitter:card', content: 'summary_large_image' },
			{ name: 'twitter:site', content: '@atomicform' },
			{ name: 'twitter:creator', content: '@atomicform' },
			{
				name: 'twitter:description',
				ccontent: event
					? `${event.eventDescription} | Powered by Atomic Lore, the first Web3 directory showcasing offchain history that is cryptographically signed, stored, and beautifully displayed.`
					: import.meta.env.VITE_APP_DESCRIPTION
						? import.meta.env.VITE_APP_DESCRIPTION
						: 'A tool to explore NFT history, by Atomic Form',
			},
			{
				name: 'twitter:title',
				content: event
					? `Atomic Lore for ${event.eventTitle}`
					: import.meta.env.VITE_APP_NAME
						? import.meta.env.VITE_APP_NAME
						: 'Atomic Lore',
			},
		]
	}
}

export async function loader({ request, params }: LoaderFunctionArgs) {
	const url = new URL(request.url)
	const origin = getDomainUrl(request)
	if (params.cid) {
		let nftData
		let metadata: AttestationType | undefined = undefined
		let nftAttestations = [] as AttestationType[] | undefined
		let eventData: eventData[] | undefined = undefined

		try {
			console.time('attestation v2')
			let retrievedMintedMetadata = await getAttestationv2({
				attestationIPFSCID: params.cid,
			})
			console.timeEnd('attestation v2')
			const returnedMetadata = retrievedMintedMetadata.attestations[0]
			if (!returnedMetadata) {
				console.error('NO RETRIEVED ATTESTATION For Metadata Profile', {
					chain: params.chain,
					contractAddress: params.contractAddress,
					tokenId: params.tokenId,
					cid: params.cid,
				})
				throw new Response('Error retrieving attestation!', {
					status: 500,
				})
			} else {
				metadata = returnedMetadata
			}
		} catch (e) {
			console.error('ERROR RETRIEVING Attestation For Metadata Profile', {
				e,
				chain: params.chain,
				contractAddress: params.contractAddress,
				tokenId: params.tokenId,
				cid: params.cid,
			})
			throw new Response('Error retrieving attestation!', {
				status: 500,
			})
		}

		if (metadata.isRevoked) {
			return redirect(`/metadataProfile/rollup/${params.cid}`, {
				status: 302,
			})
		}

		if (metadata && metadata.properties && metadata?.properties.rollupCID) {
			try {
				nftAttestations = await getDerivedAttestationsForRollupCID({
					rollupCid: metadata?.properties.rollupCID,
				})
			} catch (e) {
				console.error('ERROR GETTING ATTESTATIONS BY EVENT CID', {
					e,
					rollupCID: metadata.properties.rollupCID,
				})
			}
			if (nftAttestations) {
				try {
					const returnedEventData = await getEventDataByRollupCID(
						metadata?.properties.rollupCID,
					)
					if (returnedEventData) {
						eventData = returnedEventData
					} else {
						console.error('NO RETURNED Event Data BY EVENT CID', {
							rollupCID: metadata.properties.rollupCID,
						})
					}
				} catch (e) {
					console.error('ERROR GETTING Event Data BY EVENT CID', {
						e,
						rollupCID: metadata.properties.rollupCID,
					})
				}

				if (eventData && eventData.length) {
					nftData = (
						await Promise.all(
							eventData.map(async (nftEntry: eventData) => {
								try {
									const entryNFTData = JSON.parse(nftEntry.metadata) as NFTCache
									// @ts-ignore
									let nftData: nft = { ...entryNFTData }
									if (!nftData || !nftData.contract || !nftData.tokenId) {
										return
									}
									const relatedAttestation = nftAttestations?.find(
										attestation =>
											attestation.references.entry?.nft.contractAddress &&
											attestation.references.entry?.nft.tokenId &&
											nftData.contract.address
												? attestation.references.entry?.nft.contractAddress.toLowerCase() ==
														nftData.contract.address.toLowerCase() &&
													attestation.references.entry?.nft.tokenId.toLowerCase() ==
														nftData.tokenId.toLowerCase()
												: attestation.references.entry?.nft.tokenId &&
													attestation.references.entry?.nft.tokenId.toLowerCase() ==
														nftData.tokenId.toLowerCase(),
									)
									if (!relatedAttestation) {
										return
									}
									nftData.chain =
										relatedAttestation.references.entry &&
										relatedAttestation.references.entry.chain &&
										relatedAttestation.references.entry.chain.name
											? relatedAttestation.references.entry.chain.name
											: undefined
									if (!nftData.name) {
										nftData.name = `#${nftData.tokenId}`
									} else {
										nftData.name = formatString(nftData.name)
									}
									if (!nftData.description) {
										nftData.description = 'Description unavailable'
									} else {
										nftData.description = formatString(nftData.description)
									}
									nftData.contentType =
										entryNFTData.image && entryNFTData.image.contentType
											? entryNFTData.image.contentType
											: undefined
									if (nftData.contentType === 'video/mp4') {
										nftData.previewImage = videoPlaceholder
									} else if (
										entryNFTData.image &&
										entryNFTData.image.thumbnailUrl
									) {
										nftData.previewImage = entryNFTData.image.thumbnailUrl
									} else if (
										entryNFTData.image &&
										entryNFTData.image.originalUrl
									) {
										nftData.previewImage = entryNFTData.image.originalUrl
									} else {
										// Resize image with cloudinary
										nftData.previewImage = undefined
									}
									return nftData
								} catch (e) {
									console.error(e)
								}
							}),
						)
					).filter(elem => elem != undefined)
				} else {
					console.error('ERROR LOADING DATA FOR EVENT', { cid: params.cid })
					throw new Response('Error retrieving attestation for event!', {
						status: 500,
					})
				}
			}
		}
		return defer({
			event: metadata.event,
			user: metadata.user,
			nftData,
			origin,
			atomicLoreUrl: url.origin,
			cid: params.cid,
			metadataEntry: metadata,
			nftAttestations: nftAttestations,
		})
	}
}

export default function eventPage() {
	const {
		event,
		user,
		nftData,
		cid,
		metadataEntry,
		nftAttestations,
		atomicLoreUrl,
	} = useLoaderData<{
		event: eventData
		user: User
		nftData: nft[]
		cid: string
		metadataEntry: AttestationType
		nftAttestations: AttestationType[]
		atomicLoreUrl: string
	}>()

	return (
		<EventMetadataProfile
			event={event}
			user={user}
			nftData={nftData}
			cid={cid}
			metadataEntry={metadataEntry}
			nftAttestations={nftAttestations}
			atomicLoreUrl={atomicLoreUrl}
		/>
	)
}

export function ErrorBoundary() {
	const error = useRouteError()
	captureRemixErrorBoundaryError(error)

	if (isRouteErrorResponse(error)) {
		console.error('Error is:', error)
		return (
			<Section title={'Error Loading Attestation'}>
				<TypographyParagraph size="large">
					An error occurred while loading this Attestation
				</TypographyParagraph>
				<TypographyParagraph size="large" additionalClasses="pt-2 pb-4">
					Error Status: {error.status} - {error.statusText}
				</TypographyParagraph>
				<TypographyParagraph size="large" additionalClasses="pt-2 pb-4">
					Error Data: {error.data}
				</TypographyParagraph>
				<Link to="/" prefetch="none">
					<Button>Go Home</Button>
				</Link>
			</Section>
		)
	} else if (error instanceof Error) {
		console.error('Error is:', error)
		return (
			<Section title={'Error Loading Attestation'}>
				<TypographyParagraph size="large">
					An error occurred while loading this Attestation
				</TypographyParagraph>
				<TypographyParagraph size="large" additionalClasses="pt-2 pb-4">
					Error Message: {error.message}
				</TypographyParagraph>
				<Link to="/" prefetch="none">
					<Button>Go Home</Button>
				</Link>
			</Section>
		)
	} else {
		return (
			<Section title={'Error Loading Attestation'}>
				<TypographyParagraph size="large">
					An unknown error occurred while loading this Attestation
				</TypographyParagraph>
				<Link to="/" prefetch="none">
					<Button>Go Home</Button>
				</Link>
			</Section>
		)
	}
}
